import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import { getChartPalette, getModelColorMap } from '../../chartPalette'

const MAX_BAR_SIZE = 90
const PALETTE = getChartPalette()
const FIXED_COLORS = getModelColorMap()

function norm(s) {
  return String(s || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase()
}

function nextUnusedFrom(startIdx, usedSet) {
  for (let i = 0; i < PALETTE.length; i++) {
    const idx = (startIdx + i) % PALETTE.length
    const c = PALETTE[idx]
    if (!usedSet.has(c)) return c
  }
  return PALETTE[startIdx % PALETTE.length]
}

function buildColorMap(modelKeys = []) {
  const originals = {}
  const normalizedModels = modelKeys.map((m) => {
    const n = norm(m)
    originals[n] = m
    return n
  })
  const colorByNorm = {}
  const used = new Set()
  const present = new Set(normalizedModels)
  Object.keys(FIXED_COLORS).forEach((key) => {
    const n = norm(key)
    if (present.has(n)) {
      const c = FIXED_COLORS[key]
      colorByNorm[n] = c
      used.add(c)
    }
  })
  normalizedModels.forEach((m, i) => {
    if (!colorByNorm[m]) {
      const preferred = PALETTE[i % PALETTE.length]
      const assigned = used.has(preferred) ? nextUnusedFrom(i, used) : preferred
      colorByNorm[m] = assigned
      used.add(assigned)
    }
  })
  const colorMap = {}
  normalizedModels.forEach((n) => {
    colorMap[originals[n]] = colorByNorm[n]
  })
  return colorMap
}

const toNum = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const splitLabel = (raw) => {
  const s = String(raw || '')
  const match = s.match(/^(.*)\s\((.*)\)$/)
  return { raw: s, line1: match ? match[1] : s, line2: match ? match[2] : '' }
}

const measureText = (() => {
  let canvas
  return (text, font = '12px Arial') => {
    canvas = canvas || document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.font = font
    return ctx.measureText(String(text || '')).width
  }
})()

const AdaptiveXAxisTick = ({ x, y, payload, rotate, angle = -35 }) => {
  const { raw, line1, line2 } = splitLabel(payload?.value)

  if (rotate) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text transform={`rotate(${angle})`} textAnchor="end" fontSize={12} fill="#666" dy={10}>
          {raw}
        </text>
      </g>
    )
  }

  return (
    <g transform={`translate(${x},${y + 10})`}>
      <text textAnchor="middle" fontSize={12} fill="#666">
        <tspan x="0" dy="0">
          {line1}
        </tspan>
        {line2 && (
          <tspan x="0" dy="14">
            {line2}
          </tspan>
        )}
      </text>
    </g>
  )
}

const buildModelSoftwareAgg = (rows = []) => {
  const map = {}

  rows.forEach((r) => {
    const model = r?.model
    const type = r?.softwareType || 'Unknown'

    if (!model) return

    const total = toNum(r.totalDevices)
    const updated = toNum(r.updatedDevices)

    if (!map[model]) map[model] = { model, totalByType: {}, updatedByType: {} }

    map[model].totalByType[type] = (map[model].totalByType[type] || 0) + total
    map[model].updatedByType[type] = (map[model].updatedByType[type] || 0) + updated
  })

  const models = Object.values(map).sort((a, b) => String(a.model).localeCompare(String(b.model)))
  const allTypes = Array.from(new Set(models.flatMap((m) => Object.keys(m.totalByType)))).sort((a, b) => String(a).localeCompare(String(b)))

  const chartRows = models.map((m) => {
    const row = { model: m.model }
    allTypes.forEach((t) => {
      if (!(t in m.totalByType)) {
        row[`${t}__updatedPercent`] = null
        row[`${t}__olderPercent`] = null
        return
      }

      const total = toNum(m.totalByType[t])
      const updated = toNum(m.updatedByType[t])
      const updatedPercent = total === 0 ? 0 : (updated / total) * 100
      const olderPercent = 100 - updatedPercent

      row[`${t}__updatedPercent`] = Number(updatedPercent.toFixed(4))
      row[`${t}__olderPercent`] = Number(olderPercent.toFixed(4))
    })
    return row
  })

  return { chartRows, allTypes }
}

const buildModelOnlyAgg = (rows = []) => {
  const map = {}

  rows.forEach((r) => {
    const model = r?.model

    if (!model) return

    const totalDevices = toNum(r.totalDevices)
    const updatedDevices = toNum(r.updatedDevices)
    const apiUpdatedPercent = r.updatedPercent != null ? toNum(r.updatedPercent) : null

    if (!map[model]) {
      map[model] = { model, totalDevices: 0, updatedDevices: 0, weightedPercentSum: 0, weightedPercentWeight: 0 }
    }
    map[model].totalDevices += totalDevices
    map[model].updatedDevices += updatedDevices

    if (apiUpdatedPercent != null) {
      const pct = apiUpdatedPercent <= 1 ? apiUpdatedPercent * 100 : apiUpdatedPercent
      const weight = totalDevices > 0 ? totalDevices : 1
      map[model].weightedPercentSum += pct * weight
      map[model].weightedPercentWeight += weight
    }
  })

  return Object.values(map)
    .sort((a, b) => String(a.model).localeCompare(String(b.model)))
    .map((m) => {
      const hasApiPercent = m.weightedPercentWeight > 0
      const computed = m.totalDevices === 0 ? 0 : (m.updatedDevices / m.totalDevices) * 100
      const updatedPercent = hasApiPercent ? m.weightedPercentSum / m.weightedPercentWeight : computed
      return {
        model: m.model,
        totalDevices: m.totalDevices,
        updatedDevices: m.updatedDevices,
        updatedPercent: Number(updatedPercent.toFixed(4)),
      }
    })
}

const GroupedTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null

  const rows = payload
    .filter((p) => p?.value != null && Number.isFinite(Number(p.value)))
    .map((p) => {
      const key = String(p.dataKey || '')
      const [type, metric] = key.split('__')
      const which = metric === 'updatedPercent' ? 'Latest' : metric === 'olderPercent' ? 'Older' : ''
      return { type: type || 'Unknown', which, value: Number(p.value), color: p.color }
    })
    .filter((r) => r.which)

  if (!rows.length) return null

  const order = { Latest: 0, Older: 1 }
  rows.sort((a, b) => a.type.localeCompare(b.type) || (order[a.which] ?? 99) - (order[b.which] ?? 99))

  return (
    <div style={{ background: '#fff', border: '1px solid #ddd', padding: 10, borderRadius: 6 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {rows.map((r) => (
        <div key={`${r.type}-${r.which}`} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: r.color, display: 'inline-block' }} />
          <span style={{ color: '#333' }}>
            {r.type} - {r.which}: {r.value.toFixed(2)}%
          </span>
        </div>
      ))}
    </div>
  )
}

const SoftwareVersionChart = ({ data = [], chartType = 'bar' }) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const containerRef = useRef(null)
  const modelOnlyAgg = useMemo(() => buildModelOnlyAgg(data), [data])

  const modelColorMap = useMemo(() => {
    const modelKeys = modelOnlyAgg.map((m) => m.model)
    return buildColorMap(modelKeys)
  }, [modelOnlyAgg])

  const CustomLegend = ({ payload }) => {
    if (!payload?.length) return null
    const isModelLegend = payload.some((e) => modelColorMap[e?.payload?.name || e?.value])

    if (isModelLegend) {
      const seen = new Map()
      payload.forEach((e) => {
        const name = e?.payload?.name || e?.value
        if (!name) return
        if (!seen.has(name)) {
          seen.set(name, { name, color: modelColorMap[name] || e.color })
        }
      })

      const unique = Array.from(seen.values()).sort((a, b) => {
        const aName = String(a.name || '')
        const bName = String(b.name || '')
        if (aName === 'N/A') return 1
        if (bName === 'N/A') return -1
        return aName.localeCompare(bName)
      })

      return (
        <div className="custom-legend-container">
          {unique.map((u) => (
            <div className="legend-item" key={u.name}>
              <span className="legend-color-dot" style={{ background: u.color }} />
              <span className="legend-label">{u.name}</span>
            </div>
          ))}
        </div>
      )
    }

    return (
      <div className="custom-legend-container">
        {payload.map((entry) => (
          <div className="legend-item" key={entry.value}>
            <span className="legend-color-dot" style={{ background: entry.color }} />
            <span className="legend-label">{entry.value}</span>
          </div>
        ))}
      </div>
    )
  }

  const { chartRows, allTypes } = useMemo(() => buildModelSoftwareAgg(data), [data])

  const typeStatusColorMap = useMemo(() => {
    const map = {}
    let idx = 0
    allTypes.forEach((type) => {
      map[`${type}__Latest`] = PALETTE[idx % PALETTE.length]
      idx += 1
      map[`${type}__Older`] = PALETTE[idx % PALETTE.length]
      idx += 1
    })
    return map
  }, [allTypes])

  const shouldRotateModelAxis = useMemo(() => {
    const count = chartRows.length || 0
    if (!count) return false
    const approxPlotWidth = Math.max(0, (dimensions.width || 0) - 60)
    if (approxPlotWidth <= 0) return false
    const slot = approxPlotWidth / count
    const maxLabelWidth = chartRows.reduce((max, r) => Math.max(max, measureText(r.model, '12px Arial')), 0)
    return maxLabelWidth + 12 > slot
  }, [chartRows, dimensions.width])

  const renderModelUpdatedPie = () => {
    const pieLatest = modelOnlyAgg
      .map((m) => ({
        key: m.model,
        name: m.model,
        value: Number(m.updatedPercent) || 0,
        totalDevices: m.totalDevices,
      }))
      .filter((x) => x.totalDevices >= 0)

    const pieOlder = modelOnlyAgg
      .map((m) => {
        const latest = Number(m.updatedPercent) || 0
        return {
          key: m.model,
          name: m.model,
          value: Math.max(0, 100 - latest),
          totalDevices: m.totalDevices,
        }
      })
      .filter((x) => x.totalDevices >= 0)

    const isLatestAllZero = pieLatest.every((m) => m.value === 0)
    const isOlderAllZero = pieOlder.every((m) => m.value === 0)

    return (
      <ResponsiveContainer width="100%" height={520}>
        <PieChart>
          <Pie
            data={isLatestAllZero ? [{ name: 'N/A', value: 100 }] : pieLatest}
            dataKey="value"
            nameKey="name"
            cx="30%"
            cy="50%"
            outerRadius={170}
            isAnimationActive={false}
            label={!isLatestAllZero ? ({ value }) => `${Number(value).toFixed(2)}%` : false}
          >
            {(isLatestAllZero ? [{ name: 'N/A' }] : pieLatest).map((entry, index) => (
              <Cell key={`latest-${index}`} fill={isLatestAllZero ? '#f0f0f0' : modelColorMap[entry.name]} />
            ))}
          </Pie>

          <Pie
            data={isOlderAllZero ? [{ name: 'N/A', value: 100 }] : pieOlder}
            dataKey="value"
            nameKey="name"
            cx="70%"
            cy="50%"
            outerRadius={170}
            isAnimationActive={false}
            label={!isOlderAllZero ? ({ value }) => `${Number(value).toFixed(2)}%` : false}
          >
            {(isOlderAllZero ? [{ name: 'N/A' }] : pieOlder).map((entry, index) => (
              <Cell key={`older-${index}`} fill={isOlderAllZero ? '#f0f0f0' : modelColorMap[entry.name]} />
            ))}
          </Pie>

          <text x="30%" y="83%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 16, fontWeight: 600 }}>
            Latest Version
          </text>
          <text x="70%" y="83%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 16, fontWeight: 600 }}>
            Older Version
          </text>

          <Legend content={CustomLegend} />

          <Tooltip
            formatter={(v, _n, props) => {
              return [`${Number(v).toFixed(2)}%`, props?.payload?.name || '']
            }}
            labelFormatter={() => 'Model'}
          />
        </PieChart>
      </ResponsiveContainer>
    )
  }

  const renderGroupedBars = () => {
    const margin = { top: 20, right: 30, left: 0, bottom: shouldRotateModelAxis ? 90 : 45 }
    return (
      <BarChart data={chartRows} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="model" interval={0} height={shouldRotateModelAxis ? 110 : 65} tick={<AdaptiveXAxisTick rotate={shouldRotateModelAxis} angle={-35} />} />
        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<GroupedTooltip />} />
        <Legend content={CustomLegend} />
        {allTypes.map((type) => (
          <React.Fragment key={type}>
            <Bar
              dataKey={`${type}__updatedPercent`}
              name={`${type} Latest`}
              stackId={type}
              fill={typeStatusColorMap[`${type}__Latest`]}
              barSize={24}
              maxBarSize={MAX_BAR_SIZE}
              isAnimationActive={false}
            />
            <Bar
              dataKey={`${type}__olderPercent`}
              name={`${type} Older`}
              stackId={type}
              fill={typeStatusColorMap[`${type}__Older`]}
              barSize={24}
              maxBarSize={MAX_BAR_SIZE}
              isAnimationActive={false}
            />
          </React.Fragment>
        ))}
      </BarChart>
    )
  }

  const renderClusteredBars = () => {
    const margin = { top: 20, right: 30, left: 0, bottom: shouldRotateModelAxis ? 90 : 45 }
    return (
      <BarChart data={chartRows} margin={margin} barCategoryGap="20%" barGap={6}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="model" interval={0} height={shouldRotateModelAxis ? 110 : 65} tick={<AdaptiveXAxisTick rotate={shouldRotateModelAxis} angle={-35} />} />
        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<GroupedTooltip />} />
        <Legend content={CustomLegend} />
        {allTypes.flatMap((type) => [
          <Bar
            key={`${type}__Latest`}
            dataKey={`${type}__updatedPercent`}
            name={`${type} Latest`}
            fill={typeStatusColorMap[`${type}__Latest`]}
            barSize={12}
            maxBarSize={MAX_BAR_SIZE}
            isAnimationActive={false}
          />,
          <Bar
            key={`${type}__Older`}
            dataKey={`${type}__olderPercent`}
            name={`${type} Older`}
            fill={typeStatusColorMap[`${type}__Older`]}
            barSize={12}
            maxBarSize={MAX_BAR_SIZE}
            isAnimationActive={false}
          />,
        ])}
      </BarChart>
    )
  }

  const renderChart = () => {
    switch (chartType) {
      // case 'line':
      //   return renderGroupedLines()
      case 'stackedBar':
        return renderGroupedBars()
      case 'pie':
        return renderModelUpdatedPie()
      case 'bar':
      default:
        return renderClusteredBars()
    }
  }

  useEffect(() => {
    const observer = new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} style={{ width: '100%', height: 650 }} className={`chart-responsive-wrapper ${chartType === 'pie' ? 'pie-mode' : ''}`}>
      <div className="chart-min-width">
        {dimensions.width > 0 && dimensions.height > 0 && (
          <ResponsiveContainer width="100%" height="100%" key={`${dimensions.width}x${dimensions.height}`}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export default SoftwareVersionChart

